import * as React from 'react'
import { createContext, useContext, useState } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { clsx } from 'clsx'

import { Input as BaseInput } from '../Input/Input'
import type { InputProps as BaseInputProps } from '../Input/Input'
import { Popover } from '../Popover/Popover'
import { SwatchGroup } from '../SwatchGroup/SwatchGroup'

import { DEFAULT_COLOR, colorSwatches } from './constants'
import {
  getColorContrast,
  getColors,
  getValidHex,
  isHexValid,
} from './utils/HexValidations'

import styles from './ColorPicker.module.css'

/**
 * @internal
 */
type ColorPickerContextValue = {
  /** Current color value in hex format */
  color: string
  /** Update the color value */
  setColor: (color: string) => void
  /** Whether the current hex value is valid */
  isValidHex: boolean
  /** Update hex validation state */
  setIsValidHex: (isValid: boolean) => void
  /** Last valid color before invalid input */
  lastValidColor: string
  /** Callback triggered when color changes */
  onChange: (color: string, contrastColor: string) => void
  /** Callback triggered when color picker loses focus */
  onBlur: (color: string, contrastColor: string) => void
  /** Unified handler for color changes with validation */
  handleColorChange: (color: string) => void
}

const ColorPickerContext = createContext<ColorPickerContextValue | null>(null)

function useColorPickerContext(): ColorPickerContextValue {
  const context = useContext(ColorPickerContext)
  if (!context) {
    throw new Error(
      'ColorPicker compound components must be used within ColorPicker',
    )
  }
  return context
}

/**
 * Props for the ColorPicker root component
 */
type ColorPickerProps = {
  /** Initial color value in hex format. Defaults to '#000000'. */
  defaultColor?: string
  /** Callback triggered when the color picker loses focus. Receives the color and its contrast color. */
  onBlur: (color: string, contrastColor: string) => void
  /** Callback triggered when the color value changes. Receives the color and its contrast color. */
  onChange: (color: string, contrastColor: string) => void
  /** Child components (Trigger and Content) */
  children: ReactNode
}

type ColorPickerElement = React.ElementRef<'div'>

const ColorPicker = React.forwardRef<ColorPickerElement, ColorPickerProps>(
  ({ onChange, defaultColor = DEFAULT_COLOR, onBlur, children }, ref) => {
    const [color, setColor] = useState(defaultColor)
    const [isValidHex, setIsValidHex] = useState(true)
    const [lastValidColor, setLastValidColor] = useState(defaultColor)

    // Memoize contrast color to avoid redundant calculations
    const contrastColor = React.useMemo(() => getColorContrast(color), [color])

    const handleColorChange = (newColor: string): void => {
      const { color: validColor, contrastColor } = getColors(newColor)
      setColor(validColor)
      setLastValidColor(validColor)
      onChange(validColor, contrastColor)
    }

    const contextValue: ColorPickerContextValue = {
      color,
      setColor,
      isValidHex,
      setIsValidHex,
      lastValidColor,
      onChange,
      onBlur,
      handleColorChange,
    }

    return (
      <ColorPickerContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={styles.colorSelectorWrapper}
          style={
            {
              '--picker-color': color,
              '--contrast-color': contrastColor,
            } as React.CSSProperties
          }
          aria-label="Color picker"
        >
          <Popover
            onOpenChange={(open: boolean): void => {
              if (!open) {
                onBlur(color, contrastColor)
              }
            }}
          >
            {children}
          </Popover>
        </div>
      </ColorPickerContext.Provider>
    )
  },
)

ColorPicker.displayName = 'ColorPicker'

/**
 * Props for the ColorPreview component
 */
type ColorPreviewProps = {
  /** When true, disables the interactive color wheel picker. Defaults to false. */
  disabled?: boolean
}

type ColorPreviewElement = React.ElementRef<'div'>

const ColorPreview = React.forwardRef<ColorPreviewElement, ColorPreviewProps>(
  ({ disabled = false }, ref) => {
    const { color, handleColorChange } = useColorPickerContext()

    return (
      <div
        ref={ref}
        className={clsx(styles.colorPreview, {
          [styles.colorPreviewInteractive]: !disabled,
        })}
        style={
          {
            '--picker-color': color,
          } as React.CSSProperties
        }
        aria-label={`Current color: ${color}`}
      >
        {!disabled && (
          <input
            className={styles.colorInput}
            type="color"
            value={color}
            id="colorWheel"
            onChange={(event) => handleColorChange(event.target.value)}
            aria-label="Select color using color picker"
          />
        )}
      </div>
    )
  },
)

ColorPreview.displayName = 'ColorPicker.Preview'

const ColorValue = (): JSX.Element => {
  const { color } = useColorPickerContext()
  return <span className={styles.colorValue}>{color}</span>
}

ColorValue.displayName = 'ColorPicker.Value'

const ColorPickerTrigger = Popover.Trigger

/**
 * Props for the ColorPickerContent component
 */
type ColorPickerContentProps = {
  /** Child components to render inside the popover (typically Swatch and/or Input) */
  children: ReactNode
}

type ColorPickerContentElement = React.ElementRef<'div'>

const ColorPickerContent = React.forwardRef<
  ColorPickerContentElement,
  ColorPickerContentProps
>(({ children }, ref) => {
  return (
    <Popover.Content>
      <div
        ref={ref}
        className={styles.colorPopup}
        aria-label="Color picker options"
      >
        {children}
      </div>
    </Popover.Content>
  )
})

ColorPickerContent.displayName = 'ColorPicker.Content'

/**
 * Props for the Swatch component
 */
type ColorPickerSwatchProps = {
  /** Custom color palette as name-to-hex mapping. Defaults to the standard Buffer color palette. */
  colors?: Record<string, string>
}

const Swatch = ({ colors = colorSwatches }: ColorPickerSwatchProps) => {
  const { color, handleColorChange } = useColorPickerContext()

  return (
    <SwatchGroup
      className={styles.colorSwatchesContainer}
      value={color}
      onChange={(event): void => handleColorChange(event.target.value)}
    >
      {Object.entries(colors).map(([name, swatchColor]) => (
        <SwatchGroup.Swatch key={swatchColor} color={swatchColor} name={name} />
      ))}
    </SwatchGroup>
  )
}

Swatch.displayName = 'ColorPicker.Swatch'

/**
 * Props for the ColorPicker Input component.
 * Extends all base Input props from Popcorn.
 */
type ColorPickerInputProps = BaseInputProps

type InputElement = React.ElementRef<'input'>

const ColorPickerInput = React.forwardRef<InputElement, ColorPickerInputProps>(
  (
    {
      prefix,
      onChange: userOnChange,
      onBlur: userOnBlur,
      onKeyUp: userOnKeyUp,
      ...restProps
    },
    ref,
  ) => {
    const {
      color,
      setColor,
      isValidHex,
      setIsValidHex,
      lastValidColor,
      onChange,
      onBlur,
      handleColorChange,
    } = useColorPickerContext()

    return (
      <div className={styles.colorContainer}>
        <div className={styles.inputWrapper}>
          <BaseInput
            {...restProps}
            ref={ref}
            prefix={prefix ?? <ColorPreview />}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
              const inputValue = event.target.value
              const isValidColor = isHexValid(inputValue)

              setIsValidHex(isValidColor)
              setColor(inputValue)

              if (isValidColor) {
                const { contrastColor } = getColors(inputValue)
                onChange(inputValue, contrastColor)
              }

              userOnChange?.(event)
            }}
            value={color}
            aria-label="Hex color value"
            aria-invalid={!isValidHex}
            onBlur={(event: React.FocusEvent<HTMLInputElement>): void => {
              if (!isValidHex) {
                const selectedHex = getValidHex(color, lastValidColor)

                handleColorChange(selectedHex)
                setIsValidHex(true)
              }

              userOnBlur?.(event)
            }}
            onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>): void => {
              if (event.key === 'Enter') {
                event.preventDefault()

                const isValidColor = isHexValid(color)

                if (isValidColor) {
                  const { color: validColor, contrastColor } = getColors(color)
                  onBlur(validColor, contrastColor)
                }
              }

              userOnKeyUp?.(event)
            }}
          />
        </div>
      </div>
    )
  },
)

ColorPickerInput.displayName = 'ColorPicker.Input'

const ColorPickerObject = Object.assign(ColorPicker, {
  Trigger: ColorPickerTrigger,
  Content: ColorPickerContent,
  Swatch,
  Input: ColorPickerInput,
  Preview: ColorPreview,
  Value: ColorValue,
})

export { ColorPickerObject as ColorPicker }
export type {
  ColorPickerProps,
  ColorPickerContentProps,
  ColorPreviewProps,
  ColorPickerSwatchProps,
  ColorPickerInputProps,
}
